import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Download } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Link } from 'react-router-dom';
import JSZip from 'jszip';

interface UserProfile {
  username: string;
  bio: string;
  userId: string;
  isPrivate?: boolean;
}

const Settings: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [backupLoading, setBackupLoading] = useState<'json' | 'zip' | null>(null);

  const fetchAllBits = async () => {
    if (!user) return [];
    const q = query(collection(db, 'drafts'), where('userId', '==', user.uid));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const handleDownloadJSON = async () => {
    setBackupLoading('json');
    try {
      const bits = await fetchAllBits();
      const json = JSON.stringify(bits, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `show-your-bits-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading JSON backup:', err);
    } finally {
      setBackupLoading(null);
    }
  };

  const handleDownloadZip = async () => {
    setBackupLoading('zip');
    try {
      const bits = await fetchAllBits() as any[];
      const zip = new JSZip();
      bits.forEach((bit) => {
        const safeTitle = (bit.title || 'untitled').replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const text = [
          `TITLE: ${bit.title || 'Untitled'}`,
          ``,
          `CONTENT:`,
          bit.content || '',
          ``,
          `TAGS: ${(bit.tags || []).join(', ')}`,
          ``,
          `CREATED: ${bit.createdAt || ''}`,
          `UPDATED: ${bit.updatedAt || ''}`,
          bit.audioUrl ? `\nAUDIO URL: ${bit.audioUrl}` : '',
        ].join('\n');
        zip.file(`${safeTitle}.txt`, text);
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `show-your-bits-backup-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading ZIP backup:', err);
    } finally {
      setBackupLoading(null);
    }
  };
  const [profile, setProfile] = useState<UserProfile>({ 
    username: '', 
    bio: '', 
    userId: '',
    isPrivate: false 
  });

  useEffect(() => {
    const getProfile = async () => {
      if (!user?.uid) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as UserProfile;
          setProfile({
            ...userData,
            isPrivate: userData.isPrivate ?? false // default to public if not set
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    getProfile();
  }, [user]);

  const handlePrivacyToggle = async () => {
    if (!user?.uid) return;

    try {
      const newIsPrivate = !profile.isPrivate;
      await updateDoc(doc(db, 'users', user.uid), {
        isPrivate: newIsPrivate
      });
      setProfile(prev => ({ ...prev, isPrivate: newIsPrivate }));
    } catch (error) {
      console.error('Error updating privacy settings:', error);
    }
  };
  
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-navy-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <SettingsIcon className="mr-2" size={24} />
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>
        
        <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-navy-800' : 'bg-white'} shadow-lg`}>
          <div className="space-y-6">
            {/* Account Settings Section */}
            <section>
              <h2 className="text-xl font-semibold mb-4">Account Settings</h2>
              <div className="space-y-4">
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  Username: {profile.username}
                </p>
              </div>
            </section>

            {/* Privacy Settings Section */}
            <section>
              <h2 className="text-xl font-semibold mb-4">Privacy Settings</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} font-medium`}>
                      Private Profile
                    </p>
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
                      When enabled, only you can see your profile information
                    </p>
                  </div>
                  <button
                    onClick={handlePrivacyToggle}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      profile.isPrivate 
                        ? 'bg-orange-500' 
                        : theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                        profile.isPrivate 
                          ? 'translate-x-7' 
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div className="pt-2">
                  <Link 
                    to="/privacy"
                    className={`text-sm ${theme === 'dark' ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-700'} underline`}
                  >
                    View our Privacy Policy
                  </Link>
                </div>
              </div>
            </section>

            {/* Backup Section */}
            <section>
              <h2 className="text-xl font-semibold mb-4">Backup My Bits</h2>
              <div className={`${theme === 'dark' ? 'bg-navy-700' : 'bg-gray-50'} p-4 rounded-lg border ${theme === 'dark' ? 'border-navy-600' : 'border-gray-200'}`}>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-sm mb-4`}>
                  Download all your saved bits as a backup.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <button
                      onClick={handleDownloadZip}
                      disabled={backupLoading !== null}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                    >
                      <Download size={16} />
                      {backupLoading === 'zip' ? 'Downloading...' : 'Download as TXT (ZIP)'}
                    </button>
                    <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      One .txt file per bit. Does not include audio.
                    </p>
                  </div>
                  <div className="flex-1">
                    <button
                      onClick={handleDownloadJSON}
                      disabled={backupLoading !== null}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                    >
                      <Download size={16} />
                      {backupLoading === 'json' ? 'Downloading...' : 'Download as JSON'}
                    </button>
                    <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Single file with all data. Recommended if you have audio recordings.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Delete Account Section */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-red-500">Delete Account</h2>
              <div className="space-y-4">
                <div className={`${theme === 'dark' ? 'bg-navy-700' : 'bg-red-50'} p-4 rounded-lg border ${theme === 'dark' ? 'border-red-800' : 'border-red-200'}`}>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                    Permanently deletes your account and all associated data. This cannot be undone.
                  </p>
                  <button
                    onClick={() => { setShowCloseModal(true); setShowFinalConfirm(false); }}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </section>

            {/* Delete Account Modal — Step 1 */}
            {showCloseModal && !showFinalConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`${theme === 'dark' ? 'bg-navy-800' : 'bg-white'} p-6 rounded-lg max-w-md w-full mx-4`}>
                  <h3 className="text-xl font-bold mb-4 text-red-500">Delete your account?</h3>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                    This will permanently delete:
                  </p>
                  <ul className="list-disc list-inside mb-6 space-y-2 text-sm">
                    <li className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Your account and profile</li>
                    <li className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>All bits, ideas, and set lists</li>
                    <li className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>All exercise history and streaks</li>
                    <li className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>All recordings and uploaded files</li>
                    <li className="font-semibold text-red-500">This action is permanent and cannot be undone.</li>
                  </ul>
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => setShowCloseModal(false)}
                      className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowFinalConfirm(true)}
                      className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Yes, delete my account
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Account Modal — Step 2: Final confirmation */}
            {showCloseModal && showFinalConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`${theme === 'dark' ? 'bg-navy-800' : 'bg-white'} p-6 rounded-lg max-w-md w-full mx-4`}>
                  <h3 className="text-xl font-bold mb-4 text-red-500">Are you absolutely sure?</h3>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                    There is no way to recover your account or any of your data after this point. Click <strong>Permanently Delete</strong> to confirm.
                  </p>
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => { setShowCloseModal(false); setShowFinalConfirm(false); }}
                      className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          // Clear local data and sign out
                          localStorage.removeItem('user');
                          localStorage.removeItem('posts');
                          localStorage.removeItem('likes');
                          localStorage.removeItem('blockedUsers');
                          await signOut(auth);
                          navigate('/');
                          window.location.reload();
                        } catch (error) {
                          console.error('Error deleting account:', error);
                          alert('There was an error deleting your account. Please try again.');
                        }
                      }}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-bold"
                    >
                      Permanently Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
